<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import SearchableModel from '@/components/searchable-model/SearchableModel.vue';
import OrderItemIndexTableRow from '@/components/orders/OrderItemIndexTableRow.vue';

const props = withDefaults(defineProps<{
  class?: HTMLAttributes['class'],
  availableFilters?: Array<Record<string, any>>,
  baseFilters?: Record<string, any>,
  pushState?: boolean,
  enableContainer?: boolean,
  /**
   * Where a row opens the order. A listing embedded in another page sends it to a new
   * tab, so the reader keeps the page they were working on.
   */
  target?: '_self' | '_blank',
}>(), {
  class: undefined,
  availableFilters: () => [],
  baseFilters: () => ({}),
  pushState: false,
  enableContainer: false,
  target: '_self',
});

/** Labels are keys; SearchableModel translates them as it renders them. */
const sortOptions = [
  { value: 'created_at', label: 'Date' },
  { value: 'order_id', label: 'Order number' },
];
</script>

<template>
  <SearchableModel
    model-name="OrderItem"
    with-search
    :search-placeholder="$t('Search by order number, name, or email address')"
    :base-filters="props.baseFilters"
    :available-filters="props.availableFilters"
    :sort-options="sortOptions"
    sort="-created_at"
    :push-state="props.pushState"
    :enable-container="props.enableContainer"
    :class="props.class"
  >
    <template v-if="$slots['header-title']" #header-title>
      <slot name="header-title"></slot>
    </template>

    <template v-if="$slots['header-actions']" #header-actions>
      <slot name="header-actions"></slot>
    </template>

    <template #default="{ models }">
      <div class="table-responsive">
        <table v-if="models" class="table table-hover">
          <thead class="tw:text-left tw:text-sm tw:bg-zinc-50">
            <tr>
              <th>{{ $t('Order #') }}</th>
              <th>{{ $t('Created at') }}</th>
              <th>{{ $t('Activity') }}</th>
              <th>{{ $t('Status') }}</th>
              <th class="tw:text-right">{{ $t('Amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <OrderItemIndexTableRow
              v-for="orderItem in models"
              :key="orderItem.id"
              :order-item="orderItem"
              :target="props.target"
            />
          </tbody>
        </table>
      </div>
    </template>
  </SearchableModel>
</template>
